import { useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import { apiFetch } from '../services/api';
import { useIsManager } from '../hooks/useIsManager';

interface BoardRep {
  associate_id: number;
  name: string;
  status: string;
  scheduled_today: number;
  completed_today: number;
}

interface TeamException {
  type: string;
  associate_id: number;
  name: string;
  severity: string;
  detail: string;
}

const STATUS_LABELS: Record<string, string> = {
  in_store: 'In store',
  driving: 'Driving',
  idle: 'Idle',
  done: 'Done',
  no_show: 'No-show',
};

const STATUS_CLASSES: Record<string, string> = {
  in_store: 'bg-green-100 text-green-800',
  driving: 'bg-blue-100 text-blue-800',
  idle: 'bg-amber-100 text-amber-800',
  done: 'bg-slate-100 text-slate-600',
  no_show: 'bg-red-100 text-red-800',
};

export default function Team() {
  const isManager = useIsManager();
  const [board, setBoard] = useState<BoardRep[]>([]);
  const [exceptions, setExceptions] = useState<TeamException[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isManager) {
      setLoading(false);
      return;
    }
    let mounted = true;
    Promise.all([
      apiFetch<BoardRep[]>('/api/manager/today/'),
      apiFetch<TeamException[]>('/api/manager/exceptions/'),
    ])
      .then(([b, e]) => {
        if (!mounted) return;
        setBoard(b);
        setExceptions(e);
      })
      .catch((err) => mounted && setError(err?.message || 'Failed to load your team'))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [isManager]);

  if (!isManager) {
    return (
      <>
        <PageHeader title="My Team" />
        <div className="px-4 py-8 text-center text-slate-500">This area is for supervisors.</div>
      </>
    );
  }

  return (
    <>
      <PageHeader title="My Team" />
      <div className="space-y-5 px-4 py-4">
        {loading && <div className="text-slate-500">Loading…</div>}
        {error && <div className="rounded bg-red-50 p-3 text-red-700">{error}</div>}

        {!loading && !error && exceptions.length > 0 && (
          <section>
            <h2 className="mb-2 text-lg font-bold text-red-700">Exceptions ({exceptions.length})</h2>
            <ul className="space-y-2">
              {exceptions.map((ex, i) => (
                <li key={i} className="border-l-4 border-red-500 bg-red-50 p-3">
                  <div className="font-medium text-red-900">{ex.name}</div>
                  <div className="text-sm text-red-700">{ex.detail}</div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {!loading && !error && (
          <section>
            <h2 className="mb-2 text-lg font-bold">Today</h2>
            {board.length === 0 ? (
              <div className="text-slate-500">No reps assigned to you.</div>
            ) : (
              <ul className="space-y-2">
                {board.map((rep) => (
                  <li key={rep.associate_id} className="flex items-center justify-between rounded bg-slate-50 p-3">
                    <div>
                      <div className="font-medium">{rep.name}</div>
                      <div className="text-sm text-slate-600">
                        {rep.completed_today}/{rep.scheduled_today} calls
                      </div>
                    </div>
                    <span className={`rounded px-2 py-1 text-xs font-medium ${STATUS_CLASSES[rep.status] || 'bg-slate-100'}`}>
                      {STATUS_LABELS[rep.status] || rep.status}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </div>
    </>
  );
}
